<template>
  <div class="mall-consumption-trend">
    <el-card class="main-card glass-card">
      <template #header>
        <div class="card-header">
          <el-icon class="card-icon"><TrendCharts /></el-icon>
          <span>商城消费趋势</span>
        </div>
      </template>
      <div class="card-content">
        <!-- 图表配置区域 -->
        <div class="chart-config">
          <el-row :gutter="20" type="flex" align="middle">
            <el-col :span="8">
              <el-select v-model="timeRange" placeholder="选择时间范围" style="width: 100%;" @change="fetchData">
                <el-option label="最近7天" value="7" />
                <el-option label="最近15天" value="15" />
                <el-option label="最近30天" value="30" />
                <el-option label="最近60天" value="60" />
              </el-select>
            </el-col>
            <el-col :span="8">
              <el-select v-model="chartType" placeholder="选择图表类型" style="width: 100%;" @change="fetchData">
                <el-option label="折线图" value="line" />
                <el-option label="柱状图" value="bar" />
              </el-select>
            </el-col>
          </el-row>
        </div>
        
        <!-- 图表显示区域 -->
        <div class="chart-container" v-loading="loading" element-loading-text="加载中..." element-loading-background="rgba(255, 255, 255, 0.8)">
          <canvas ref="chartCanvas" style="width: 100%; height: 400px;"></canvas>
        </div>
        
        <!-- 数据统计标题 -->
        <div class="stats-title" v-loading="loading" element-loading-text="加载中..." element-loading-background="rgba(255, 255, 255, 0.8)">
          <h3>{{ timeRange }}天数据总览</h3>
        </div>
        
        <!-- 数据统计卡片 -->
        <el-row :gutter="20" style="margin-top: 20px;" v-loading="loading" element-loading-text="加载中..." element-loading-background="rgba(255, 255, 255, 0.8)">
          <el-col :span="6">
            <el-card class="stat-card">
              <div class="stat-item">
                <div class="stat-label">消费总数</div>
                <div class="stat-value">{{ totalConsumptions }}</div>
              </div>
            </el-card>
          </el-col>
          <el-col :span="6">
            <el-card class="stat-card">
              <div class="stat-item">
                <div class="stat-label">总消费金额</div>
                <div class="stat-value">¥{{ totalConsumptionAmount.toFixed(2) }}</div>
              </div>
            </el-card>
          </el-col>
          <el-col :span="6">
            <el-card class="stat-card">
              <div class="stat-item">
                <div class="stat-label">平均消费金额</div>
                <div class="stat-value">¥{{ avgConsumptionAmount.toFixed(2) }}</div>
              </div>
            </el-card>
          </el-col>
          <el-col :span="6">
            <el-card class="stat-card">
              <div class="stat-item">
                <div class="stat-label">日平均消费数</div>
                <div class="stat-value">{{ avgDailyConsumptions.toFixed(1) }}</div>
              </div>
            </el-card>
          </el-col>
        </el-row>
      </div>
    </el-card>
  </div>
</template>

<script>
import { ref, onMounted, watch, nextTick } from 'vue';
import { TrendCharts } from '@element-plus/icons-vue';
import * as authApi from '../../api/auth';

export default {
  name: 'MallConsumptionTrend',
  components: {
    TrendCharts
  },
  setup() {
    // 图表数据
    const consumptionTrends = ref([]);
    const timeRange = ref('7'); // 默认显示最近7天
    const chartType = ref('line'); // 默认显示折线图
    const chartCanvas = ref(null);
    
    // 悬停状态
    const hoverIndex = ref(null);
    const tooltip = ref({ show: false, x: 0, y: 0, data: null });
    
    // 统计数据
    const totalConsumptions = ref(0);
    const totalConsumptionAmount = ref(0);
    const avgConsumptionAmount = ref(0);
    const avgDailyConsumptions = ref(0);
    
    // loading状态
    const loading = ref(false);
    
    // 获取商城消费趋势数据
    const fetchData = async () => {
      try {
        // 显示loading
        loading.value = true;
        
        // 这里调用后端API获取消费趋势数据
        // 暂时使用现有的dashboard数据，后续可以添加专门的API
        const response = await authApi.getDashboardData(parseInt(timeRange.value));
        if (response.code === 200 && response.data) {
          // 使用消费趋势数据
          consumptionTrends.value = response.data.consumption_trends || [];
          
          // 计算统计数据
          calculateStats();
          
          // 绘制图表
          nextTick(() => {
            drawChart();
          });
        }
      } catch (error) {
        console.error('获取商城消费趋势数据失败:', error);
      } finally {
        // 隐藏loading
        loading.value = false;
      }
    };
    
    // 计算统计数据
    const calculateStats = () => {
      if (consumptionTrends.value.length === 0) {
        totalConsumptions.value = 0;
        totalConsumptionAmount.value = 0;
        avgConsumptionAmount.value = 0;
        avgDailyConsumptions.value = 0;
        return;
      }
      
      // 计算消费总数
      totalConsumptions.value = consumptionTrends.value.reduce((sum, item) => sum + item.count, 0);
      
      // 计算总消费金额
      totalConsumptionAmount.value = consumptionTrends.value.reduce((sum, item) => sum + parseFloat(item.amount), 0);
      
      // 计算平均消费金额
      avgConsumptionAmount.value = totalConsumptions.value > 0 ? totalConsumptionAmount.value / totalConsumptions.value : 0;
      
      // 计算日平均消费数
      avgDailyConsumptions.value = totalConsumptions.value / consumptionTrends.value.length;
    };
    
    // 绘制图表
    const drawChart = () => {
      const canvas = chartCanvas.value;
      if (!canvas || consumptionTrends.value.length === 0) return;
      
      const ctx = canvas.getContext('2d');
      const width = canvas.offsetWidth;
      const height = canvas.offsetHeight;
      
      // 获取设备像素比，解决高分辨率屏幕下图表模糊问题
      const dpr = window.devicePixelRatio || 1;
      
      // 设置canvas实际尺寸（考虑DPR）
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      
      // 设置CSS尺寸，保持视觉大小不变
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      
      // 缩放绘图上下文，确保所有绘图操作考虑DPR
      ctx.scale(dpr, dpr);
      
      // 清空画布
      ctx.clearRect(0, 0, width, height);
      
      // 计算数据范围
      const maxAmount = Math.max(...consumptionTrends.value.map(item => parseFloat(item.amount)));
      const minAmount = Math.min(...consumptionTrends.value.map(item => parseFloat(item.amount)));
      
      // 设置边距，增加右侧边距避免内容被截断
      const margin = { top: 20, right: 40, bottom: 60, left: 60 };
      const chartWidth = width - margin.left - margin.right;
      const chartHeight = height - margin.top - margin.bottom;
      
      // 计算坐标转换函数
      const xScale = (index) => margin.left + (index / (consumptionTrends.value.length - 1)) * chartWidth;
      const amountYScale = (amount) => margin.top + chartHeight - ((amount - minAmount) / (maxAmount - minAmount)) * chartHeight;
      
      // 绘制坐标轴
      ctx.strokeStyle = '#e4e7ed';
      ctx.lineWidth = 1;
      
      // X轴
      ctx.beginPath();
      ctx.moveTo(margin.left, margin.top + chartHeight);
      ctx.lineTo(margin.left + chartWidth, margin.top + chartHeight);
      ctx.stroke();
      
      // Y轴
      ctx.beginPath();
      ctx.moveTo(margin.left, margin.top);
      ctx.lineTo(margin.left, margin.top + chartHeight);
      ctx.stroke();
      
      // 绘制网格线
      ctx.strokeStyle = '#f0f2f5';
      ctx.lineWidth = 0.5;
      
      // 垂直网格线
      consumptionTrends.value.forEach((item, index) => {
        const x = xScale(index);
        ctx.beginPath();
        ctx.moveTo(x, margin.top);
        ctx.lineTo(x, margin.top + chartHeight);
        ctx.stroke();
      });
      
      // 水平网格线（5条）
      for (let i = 0; i <= 5; i++) {
        const y = margin.top + (i / 5) * chartHeight;
        ctx.beginPath();
        ctx.moveTo(margin.left, y);
        ctx.lineTo(margin.left + chartWidth, y);
        ctx.stroke();
      }
      
      // 绘制数据
      if (chartType.value === 'line') {
        // 绘制消费金额折线
        ctx.strokeStyle = '#48bb78';
        ctx.lineWidth = 2;
        ctx.beginPath();
        
        consumptionTrends.value.forEach((item, index) => {
          const x = xScale(index);
          const y = amountYScale(parseFloat(item.amount));
          if (index === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        });
        ctx.stroke();
        
        // 绘制数据点
        ctx.fillStyle = '#48bb78';
        consumptionTrends.value.forEach((item, index) => {
          const x = xScale(index);
          const y = amountYScale(parseFloat(item.amount));
          ctx.beginPath();
          ctx.arc(x, y, 4, 0, 2 * Math.PI);
          ctx.fill();
        });
      } else {
        // 绘制柱状图
        const barWidth = chartWidth / consumptionTrends.value.length * 0.6;
        ctx.fillStyle = '#48bb78';
        
        consumptionTrends.value.forEach((item, index) => {
          const x = xScale(index) - barWidth / 2;
          const y = amountYScale(parseFloat(item.amount));
          const barHeight = margin.top + chartHeight - y;
          ctx.fillRect(x, y, barWidth, barHeight);
        });
      }
      
      // 绘制日期标签
      ctx.fillStyle = '#606266';
      ctx.font = '12px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      consumptionTrends.value.forEach((item, index) => {
        const x = xScale(index);
        const y = margin.top + chartHeight + 10;
        ctx.fillText(item.date, x, y);
      });
      
      // 绘制消费金额标签
      ctx.fillStyle = '#48bb78';
      ctx.font = '12px sans-serif';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      const amountLabels = 5;
      for (let i = 0; i <= amountLabels; i++) {
        const y = margin.top + (i / amountLabels) * chartHeight;
        const amount = minAmount + (maxAmount - minAmount) * (1 - i / amountLabels);
        ctx.fillText(`¥${parseFloat(amount).toFixed(0)}`, margin.left - 10, y);
      }
      
      // 绘制图表标题
      ctx.fillStyle = '#2c3e50';
      ctx.font = '16px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText('商城消费趋势', width / 2, 10);
      
      // 绘制Y轴标题
      ctx.fillStyle = '#606266';
      ctx.font = '14px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.save();
      ctx.translate(margin.left / 2, height / 2);
      ctx.rotate(-Math.PI / 2);
      ctx.fillText('消费金额', 0, 0);
      ctx.restore();
      
      // 绘制tooltip
      if (tooltip.value.show && tooltip.value.data) {
        const tooltipData = tooltip.value.data;
        const tooltipX = tooltip.value.x;
        const tooltipY = tooltip.value.y;
        
        // 绘制tooltip背景
        const tooltipContent = [
          `日期: ${tooltipData.date}`,
          `消费数量: ${tooltipData.count}`,
          `消费金额: ¥${parseFloat(tooltipData.amount).toFixed(2)}`
        ];
        
        // 计算tooltip尺寸
        const padding = 10;
        const lineHeight = 20;
        const tooltipWidth = 180;
        const tooltipHeight = tooltipContent.length * lineHeight + padding * 2;
        
        // 调整tooltip位置，避免超出画布
        let drawX = tooltipX + 10;
        let drawY = tooltipY - tooltipHeight - 10;
        
        if (drawX + tooltipWidth > width) {
          drawX = tooltipX - tooltipWidth - 10;
        }
        
        if (drawY < 0) {
          drawY = tooltipY + 10;
        }
        
        // 绘制tooltip框
        ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
        ctx.fillRect(drawX, drawY, tooltipWidth, tooltipHeight);
        
        // 绘制tooltip文本
        ctx.fillStyle = '#ffffff';
        ctx.font = '12px sans-serif';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        
        tooltipContent.forEach((line, index) => {
          ctx.fillText(line, drawX + padding, drawY + padding + (index + 0.5) * lineHeight);
        });
      }
    };
    
    // 处理鼠标移动事件
    const handleMouseMove = (event) => {
      const canvas = chartCanvas.value;
      if (!canvas || consumptionTrends.value.length === 0) return;
      
      const rect = canvas.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;
      
      // 获取图表尺寸和边距
      const width = canvas.offsetWidth;
      const height = canvas.offsetHeight;
      const margin = { top: 20, right: 40, bottom: 60, left: 60 };
      const chartWidth = width - margin.left - margin.right;
      const chartHeight = height - margin.top - margin.bottom;
      
      // 计算数据范围
      const maxAmount = Math.max(...consumptionTrends.value.map(item => parseFloat(item.amount)));
      const minAmount = Math.min(...consumptionTrends.value.map(item => parseFloat(item.amount)));
      
      // 计算坐标转换函数
      const xScale = (index) => margin.left + (index / (consumptionTrends.value.length - 1)) * chartWidth;
      const amountYScale = (amount) => margin.top + chartHeight - ((amount - minAmount) / (maxAmount - minAmount)) * chartHeight;
      
      // 检测是否悬停在数据点上
      const pointRadius = 8; // 检测半径
      let hovered = false;
      
      for (let i = 0; i < consumptionTrends.value.length; i++) {
        const item = consumptionTrends.value[i];
        const pointX = xScale(i);
        const pointY = amountYScale(parseFloat(item.amount));
        
        // 计算鼠标到数据点的距离
        const distance = Math.sqrt(Math.pow(mouseX - pointX, 2) + Math.pow(mouseY - pointY, 2));
        
        if (distance <= pointRadius) {
          // 悬停在数据点上
          hoverIndex.value = i;
          tooltip.value = {
            show: true,
            x: pointX,
            y: pointY,
            data: item
          };
          hovered = true;
          break;
        }
      }
      
      // 如果没有悬停在任何数据点上，隐藏tooltip
      if (!hovered && hoverIndex.value !== null) {
        hoverIndex.value = null;
        tooltip.value.show = false;
      }
      
      drawChart(); // 重新绘制图表以显示或隐藏 tooltip
    };
    
    // 处理鼠标离开事件
    const handleMouseLeave = () => {
      if (hoverIndex.value !== null) {
        hoverIndex.value = null;
        tooltip.value.show = false;
        drawChart(); // 重新绘制图表以隐藏 tooltip
      }
    };
    
    // 监听数据变化，重新绘制图表
    watch([consumptionTrends, chartType], () => {
      nextTick(() => {
        drawChart();
      });
    }, { deep: true });
    
    // 监听窗口大小变化，重新绘制图表
    onMounted(() => {
      // 获取数据
      fetchData();
      
      // 监听窗口大小变化
      window.addEventListener('resize', drawChart);
      
      // 添加鼠标事件监听
      const canvas = chartCanvas.value;
      if (canvas) {
        canvas.addEventListener('mousemove', handleMouseMove);
        canvas.addEventListener('mouseleave', handleMouseLeave);
      }
    });
    
    return {
      consumptionTrends,
      timeRange,
      chartType,
      chartCanvas,
      totalConsumptions,
      totalConsumptionAmount,
      avgConsumptionAmount,
      avgDailyConsumptions,
      fetchData,
      loading
    };
  }
};
</script>

<style scoped>
.mall-consumption-trend {
  padding: 20px;
  background: #ffffff;
  min-height: calc(100vh - 60px);
}

.main-card {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  align-items: center;
  font-weight: 600;
}

.card-icon {
  font-size: 20px;
  margin-right: 8px;
  color: #48bb78;
}

.card-content {
  padding: 20px 0;
}

.chart-config {
  margin-bottom: 20px;
  padding: 10px;
  background: #f5f7fa;
  border-radius: 8px;
}

.chart-container {
  margin-bottom: 20px;
  padding: 10px;
  background: #f5f7fa;
  border-radius: 8px;
}

.stat-card {
  height: 100%;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px 0;
}

.stat-label {
  font-size: 14px;
  color: #606266;
  margin-bottom: 8px;
}

.stats-title {
  margin-top: 20px;
  margin-bottom: 10px;
}

.stats-title h3 {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
  padding: 0;
}

.stat-value {
  font-size: 24px;
  font-weight: 600;
  color: #2c3e50;
}
</style>